import { IntlShape, MessageDescriptor } from 'react-intl';
import { parseReelsPlanDayKey } from 'fym-shared/src/music24/reelsPlan';
import { RolloutDayKey, RolloutKind, rolloutDays } from 'fym-shared/src/music24/releaseRollout';
import { legacyPlanMessages } from './legacyPlanMessages';
import { planMessages } from './planMessages';
import { reelsPlanMessages } from './reelsPlan/reelsPlanMessages';

/** The countdown days of the release rollout keys. */
const COUNTDOWN_DAYS: Record<string, number> = { countdown7: 7, countdown3: 3, countdown1: 1 };

/** The day title of a release rollout day (design f1: Announce, Teaser, Countdown, Out now). */
const RELEASE_DAY_TITLES: Record<string, MessageDescriptor> = {
  announce: legacyPlanMessages.titleAnnounce,
  teaser: legacyPlanMessages.titleTeaser,
  outNow: legacyPlanMessages.titleOutNow,
  canvasTask: legacyPlanMessages.titleCanvas,
  secondReel: legacyPlanMessages.titleSecondReel,
};

/**
 * The title of a rollout day by its kind and key ("Announce", "Countdown 7", "AI reel"). The web
 * Calendar names a rollout day with it; a push day is named after what it posts.
 */
export function rolloutDayTitle(intl: IntlShape, kind: RolloutKind, key: RolloutDayKey): string {
  const asset = rolloutDays(kind).find((day) => day.key === key)?.asset ?? null;
  return planDayTitle(intl, { key, asset });
}

/** The name of an asset type in the plan lines ("Story", "AI reel"). */
const ASSET_NAMES: Record<string, MessageDescriptor> = {
  story: planMessages.assetStory,
  feedPost: planMessages.assetFeedPost,
  aiReel: planMessages.assetAiReel,
  artworkInMotion: planMessages.assetArtworkInMotion,
  lyricCard: planMessages.assetLyricCard,
  canvasLoop: planMessages.assetCanvas,
  countdown: planMessages.assetShort,
  lyricMoment: planMessages.assetShort,
  visualizer: planMessages.assetFullVideo,
  lyricVideo: planMessages.assetFullVideo,
  musicVideo: planMessages.assetFullVideo,
};

/** The name of an asset type; an image for any type the plan does not name. */
export function planAssetName(intl: IntlShape, themeId: string): string {
  return intl.formatMessage(ASSET_NAMES[themeId] ?? planMessages.assetImage);
}

/**
 * The title of a rollout day. A push week is named after what it posts. A day of an AI reels plan
 * (key `20261014-d4`) is "AI reel, day 5": the Posts lists and the Calendar name it with no plan.
 */
export function planDayTitle(intl: IntlShape, day: { key: string; asset: { themeId: string } | null }): string {
  const reelsDay = parseReelsPlanDayKey(day.key);
  if (reelsDay !== null) {
    // The second and the third post of a day (`20261014-d4p2`) name their post too.
    return reelsDay.post > 1
      ? intl.formatMessage(reelsPlanMessages.stepDayPostTitle, { day: reelsDay.offsetDays + 1, number: reelsDay.post })
      : intl.formatMessage(reelsPlanMessages.stepDayTitle, { number: reelsDay.offsetDays + 1 });
  }
  const countdown = COUNTDOWN_DAYS[day.key];
  if (countdown !== undefined) {
    return intl.formatMessage(legacyPlanMessages.titleCountdown, { days: countdown });
  }
  const title = RELEASE_DAY_TITLES[day.key];
  if (title !== undefined) {
    return intl.formatMessage(title);
  }
  return day.asset === null ? intl.formatMessage(planMessages.textPost) : planAssetName(intl, day.asset.themeId);
}

/** "−14", "+7", "Day 0" (a real minus sign, as in the design). */
export function planOffsetLabel(intl: IntlShape, offsetDays: number): string {
  if (offsetDays === 0) {
    return intl.formatMessage(planMessages.dayZero);
  }
  return offsetDays < 0 ? `−${Math.abs(offsetDays)}` : `+${offsetDays}`;
}

/** The plan facts that name a step: the kind, and the days in their order. */
interface StepNamePlan {
  kind: RolloutKind;
  days: ReadonlyArray<{ key: string }>;
}

/**
 * The title of a step inside its run sheet. A step of an AI reels plan is "AI reel N", N being its
 * place in the plan (design rollout-plan f3); every other step has its day title.
 */
export function planStepTitle(
  intl: IntlShape,
  plan: StepNamePlan,
  day: { key: string; asset: { themeId: string } | null }
): string {
  if (plan.kind !== 'reels') {
    return planDayTitle(intl, day);
  }
  const index = plan.days.findIndex((one) => one.key === day.key);
  if (index === -1) {
    return planDayTitle(intl, day);
  }
  return intl.formatMessage(reelsPlanMessages.stepTitle, { number: index + 1 });
}

/**
 * The place of a post of an AI reels plan in its day: `{ number: 2, count: 3 }` for the second of
 * three posts. The posts of one day share the start day and the offset of their keys; the post
 * number is the key suffix (`20261014-d4p2`). Null for a key of another form, or for a day that
 * has one post only.
 */
export function reelsStepPostOfDay(
  days: ReadonlyArray<{ key: string }>,
  key: string
): { number: number; count: number } | null {
  const own = parseReelsPlanDayKey(key);
  if (own === null) {
    return null;
  }
  const count = days.filter((one) => {
    const other = parseReelsPlanDayKey(one.key);
    return other !== null && other.startDay === own.startDay && other.offsetDays === own.offsetDays;
  }).length;
  return count > 1 ? { number: own.post, count } : null;
}

/**
 * The day label of a step inside its run sheet: "Day 5" (offset + 1) for an AI reels plan, else the
 * offset. A day of an AI reels plan with more than one post names the post: "Day 5 · Post 2 of 3".
 */
export function planStepWhen(intl: IntlShape, plan: StepNamePlan, offsetDays: number, key?: string): string {
  if (plan.kind === 'reels') {
    const post = key === undefined ? null : reelsStepPostOfDay(plan.days, key);
    if (post !== null) {
      return intl.formatMessage(reelsPlanMessages.stepDayPost, {
        day: offsetDays + 1,
        number: post.number,
        count: post.count,
      });
    }
    return intl.formatMessage(reelsPlanMessages.stepDay, { number: offsetDays + 1 });
  }
  return planOffsetLabel(intl, offsetDays);
}

/** "Post 2 of 3" for a step of an AI reels plan whose day has more posts; null for every other step. */
export function planStepPost(intl: IntlShape, plan: StepNamePlan, key: string): string | null {
  if (plan.kind !== 'reels') {
    return null;
  }
  const post = reelsStepPostOfDay(plan.days, key);
  return post === null ? null : intl.formatMessage(reelsPlanMessages.stepPostOf, post);
}

/** "Thu 9 Oct" of a `YYYY-MM-DD` day. */
export function planDayDate(intl: IntlShape, day: string): string {
  return intl.formatDate(new Date(`${day.slice(0, 10)}T00:00:00.000Z`), {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

/** "Thu 9 Oct, 18:00" of an ISO time, in the viewer's zone. */
export function planPostTime(intl: IntlShape, iso: string): string {
  const at = new Date(iso);
  const day = intl.formatDate(at, { weekday: 'short', day: 'numeric', month: 'short' });
  const time = intl.formatTime(at, { hour: '2-digit', minute: '2-digit' });
  return `${day}, ${time}`;
}
